import { getLocalTimeZone, parseDate, today } from "@internationalized/date";

export enum DateFilterValue {
  Equal = "=",
  NotEqual = "!=",
  LessThan = "<",
  GreaterThan = ">",
  GreaterThanOrEqual = ">=",
  LessThanOrEqual = "<=",
  Contains = "=*",
  NotContains = "!*",
  StartsWith = "^",
  NotStartsWith = "!^",
  EndsWith = "$",
  NotEndsWith = "!$",
}

function parseScheduledCalendarDayString(scheduledDate: string) {
  const datePart = scheduledDate.length >= 10 ? scheduledDate.slice(0, 10) : scheduledDate;
  return parseDate(datePart);
}

/**
 * Maps a lesson’s scheduled calendar day (`YYYY-MM-DD` from the start flow, or ISO datetime) to
 * the lessons overview URL params. Uses the same day buckets as
 * `groupLessonsByScheduledCalendarDay` (past / today / tomorrow / future).
 */
export function getLessonsOverviewSearchParamsForScheduledCalendarDay(
  scheduledDate: string | null | undefined,
  timeZone = getLocalTimeZone(),
) {
  const todayD = today(timeZone);
  const tomorrowD = todayD.add({ days: 1 });

  const day = scheduledDate ? parseScheduledCalendarDayString(scheduledDate) : todayD;

  const cmpToday = day.compare(todayD);
  if (cmpToday < 0) {
    return { date: String(todayD), dateFilterValue: DateFilterValue.LessThan };
  }
  if (cmpToday === 0) {
    return { date: String(todayD), dateFilterValue: DateFilterValue.Equal };
  }
  if (day.compare(tomorrowD) === 0) {
    return { date: String(tomorrowD), dateFilterValue: DateFilterValue.Equal };
  }
  return { date: String(todayD), dateFilterValue: DateFilterValue.GreaterThan };
}

// Filter configuration
export interface DateFilterOption {
  value: DateFilterValue;
  label: string;
  getDate: () => string;
  getTabValue: () => string;
}

// Helper function to get tomorrow's date
const getTomorrowDate = () => {
  const todayDate = today(getLocalTimeZone());
  return todayDate.add({ days: 1 });
};

// Filter options configuration
export const dateFilterOptions: DateFilterOption[] = [
  {
    value: DateFilterValue.LessThan,
    label: "Verleden",
    getDate: () => String(today(getLocalTimeZone())),
    getTabValue: () => `${DateFilterValue.LessThan}:${String(today(getLocalTimeZone()))}`,
  },
  {
    value: DateFilterValue.Equal,
    label: "Vandaag",
    getDate: () => String(today(getLocalTimeZone())),
    getTabValue: () => `${DateFilterValue.Equal}:${String(today(getLocalTimeZone()))}`,
  },
  {
    value: DateFilterValue.Equal,
    label: "Morgen",
    getDate: () => String(getTomorrowDate()),
    getTabValue: () => `${DateFilterValue.Equal}:${String(getTomorrowDate())}`,
  },
  {
    value: DateFilterValue.GreaterThan,
    label: "Toekomst",
    getDate: () => String(today(getLocalTimeZone())),
    getTabValue: () => `${DateFilterValue.GreaterThan}:${String(today(getLocalTimeZone()))}`,
  },
  {
    value: DateFilterValue.GreaterThanOrEqual,
    label: "Alles",
    getDate: () => "",
    getTabValue: () => `${DateFilterValue.GreaterThanOrEqual}:`,
  },
];

/** Past (Verleden) and “Alles” use the table layout; other tabs use cards. */
export function isLessonsOverviewTableTab(params: {
  date: string;
  dateFilterValue: DateFilterValue;
}): boolean {
  if (params.dateFilterValue === DateFilterValue.LessThan) return true;
  if (params.dateFilterValue === DateFilterValue.GreaterThanOrEqual && params.date === "") {
    return true;
  }
  return false;
}

export type LessonsOverviewCardTab = "today" | "tomorrow" | "future";

/** Which card tab (vandaag / morgen / toekomst) matches the URL; `null` when Verleden or Alles (table). */
export function getLessonsOverviewCardTab(
  params: { date: string; dateFilterValue: DateFilterValue },
  timeZone = getLocalTimeZone(),
): LessonsOverviewCardTab | null {
  if (isLessonsOverviewTableTab(params)) return null;
  if (params.dateFilterValue === DateFilterValue.GreaterThan) return "future";
  if (params.dateFilterValue === DateFilterValue.Equal) {
    const todayStr = String(today(timeZone));
    const tomorrowStr = String(today(timeZone).add({ days: 1 }));
    if (params.date === todayStr) return "today";
    if (params.date === tomorrowStr) return "tomorrow";
  }
  return "today";
}
